import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type UseBulkSelectionItemToggleSelectionOptions = {
  itemIds: number[];
  selectedItemIds: Record<number, boolean>;
  setSelectedItemIds: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
};

const useBulkSelectionToggleItemSelection = ({
  itemIds,
  selectedItemIds,
  setSelectedItemIds,
}: UseBulkSelectionItemToggleSelectionOptions) => {
  const [lastSelectedItemId, setLastSelectedItemId] = useState<number | null>(null);

  const toggleSingleItem = useCallback(
    (itemId: number) => {
      setSelectedItemIds((prevSelectedItemIds) => ({
        ...prevSelectedItemIds,
        [itemId]: prevSelectedItemIds[itemId] === undefined ? true : !prevSelectedItemIds[itemId],
      }));
    },
    [
      setSelectedItemIds,
    ],
  );

  const toggleRangeSelection = useCallback(
    (itemId: number, lastSelectedItemId: number) => {
      if (!itemIds?.length) {
        // Fallback to single selection if no order is provided
        toggleSingleItem(itemId);
        return;
      }

      const lastSelectedIndex = itemIds.findIndex((id) => id === lastSelectedItemId);
      const currentIndex = itemIds.findIndex((id) => id === itemId);

      if (lastSelectedIndex === -1 || currentIndex === -1) {
        toggleSingleItem(itemId);
        return;
      }

      const currentItemIsSelected = selectedItemIds[itemId];

      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);

      setSelectedItemIds((prevSelectedItemIds) => {
        const newSelectedItemIds = {
          ...prevSelectedItemIds,
        };
        for (let i = start; i <= end; i += 1) {
          const itemIdToToggle = itemIds[i];
          newSelectedItemIds[itemIdToToggle] = !currentItemIsSelected;
        }

        return newSelectedItemIds;
      });
    },
    [
      itemIds,
      selectedItemIds,
      setSelectedItemIds,
      toggleSingleItem,
    ],
  );

  const toggleItemSelection = useCallback(
    (itemId: number, event: React.MouseEvent<HTMLDivElement>) => {
      if (event.shiftKey && lastSelectedItemId) {
        event.preventDefault();
        event.stopPropagation();

        toggleRangeSelection(itemId, lastSelectedItemId);
      } else {
        toggleSingleItem(itemId);
      }

      setLastSelectedItemId(itemId);
    },
    [
      lastSelectedItemId,
      toggleRangeSelection,
      toggleSingleItem,
    ],
  );

  return {
    toggleItemSelection,
  };
};

type UseBulkSelectionOptions = {
  /**
   * Array of item IDs in their display order.
   * Required for shift-click range selection to work correctly.
   */
  itemIds: number[];
};

/**
 * @description
 * Creates state and functions to manage the bulk selection of items.
 */
export const useBulkSelection = ({ itemIds }: UseBulkSelectionOptions) => {
  const [selectedItemIds, setSelectedItemIds] = useState<Record<number, boolean>>({});

  const isItemSelected = useCallback(
    (itemId: number) => {
      return selectedItemIds[itemId];
    },
    [
      selectedItemIds,
    ],
  );

  const isAllItemsSelected = useCallback(() => {
    return itemIds.every((itemId) => selectedItemIds[itemId]);
  }, [
    itemIds,
    selectedItemIds,
  ]);

  const isSomeItemsSelected = useCallback(() => {
    return itemIds.some((itemId) => selectedItemIds[itemId]);
  }, [
    itemIds,
    selectedItemIds,
  ]);

  const clearSelection = useCallback(() => {
    setSelectedItemIds({});
  }, []);

  const { toggleItemSelection } = useBulkSelectionToggleItemSelection({
    itemIds,
    selectedItemIds,
    setSelectedItemIds,
  });

  const toggleSelectAllItems = useCallback(() => {
    // if all items are selected, clear the selection
    // if some items are selected, clear the selection
    // if no items are selected, select all
    if (isSomeItemsSelected()) {
      clearSelection();
    } else {
      setSelectedItemIds(
        itemIds.reduce(
          (acc, itemId) => ({
            ...acc,
            [itemId]: true,
          }),
          {},
        ),
      );
    }
  }, [
    itemIds,
    isSomeItemsSelected,
    clearSelection,
  ]);

  const bulkSelection = useMemo(
    () => ({
      isAllItemsSelected,
      isItemSelected,
      isSomeItemsSelected,
      selectedItemIds,
      setSelectedItemIds,
      clearSelection,
      toggleItemSelection,
      toggleSelectAllItems,
    }),
    [
      clearSelection,
      isAllItemsSelected,
      isItemSelected,
      isSomeItemsSelected,
      selectedItemIds,
      toggleItemSelection,
      toggleSelectAllItems,
    ],
  );

  return bulkSelection;
};

type BulkSelectionProviderContextType = {
  isAllItemsSelected: () => boolean;
  isItemSelected: (itemId: number) => boolean;
  isSomeItemsSelected: () => boolean;
  selectedItemIds: Record<number, boolean>;
  setSelectedItemIds: Dispatch<SetStateAction<Record<number, boolean>>>;
  clearSelection: () => void;
  toggleItemSelection: (itemId: number, event: React.MouseEvent<HTMLDivElement>) => void;
  toggleSelectAllItems: () => void;
};

export const BulkSelectionContext = createContext<BulkSelectionProviderContextType | null>(null);

export const useBulkSelectionContext = () => {
  const context = useContext(BulkSelectionContext);

  if (!context) {
    throw new Error('useBulkSelectionContext must be used within a BulkSelectionContext provider');
  }

  return context;
};
